/**
 * Featured deals carousel: a looping ring with the selected deal centered and one deal peeking on
 * each side. Positions are set as data-pos on each card ("0", "-1", "1", "hidden-left",
 * "hidden-right"); sections/featured-deals.liquid turns those into transforms.
 *
 * Moves on: prev/next buttons, clicking a side card, ←/→ keys, dragging (touch or mouse, the cards
 * follow the pointer), sideways trackpad scrolling / Shift + wheel, and selecting a block in the
 * theme editor.
 */

const DRAG_THRESHOLD = 8; // px before a press becomes a drag (so plain clicks still work)
const SWIPE_DISTANCE = 50; // px of drag needed to move to the next deal
const WHEEL_DISTANCE = 60; // accumulated sideways wheel delta needed to move one deal
const WHEEL_COOLDOWN = 450; // ms to ignore trackpad momentum after a move
class DealCarousel extends HTMLElement {
  connectedCallback() {
    this.cards = [...this.querySelectorAll('[data-deal]')];
    this.stage = this.querySelector('[data-deal-stage]');
    this.status = this.querySelector('[data-deal-status]');
    this.active = 0;
    if (!this.cards.length) return;
    this.riseOnView();

    this.querySelector('[data-deal-prev]')?.addEventListener('click', () => this.go(this.active - 1));
    this.querySelector('[data-deal-next]')?.addEventListener('click', () => this.go(this.active + 1));

    // Clicking a peeking side card brings it to the center instead of following its link.
    this.stage.addEventListener('click', (event) => {
      if (this.swiped) {
        this.swiped = false;
        event.preventDefault();
        return;
      }
      const card = event.target.closest('[data-deal]');
      if (!card || card.dataset.pos === '0') return;
      event.preventDefault();
      this.go(this.cards.indexOf(card));
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.go(this.active - 1);
      else if (event.key === 'ArrowRight') this.go(this.active + 1);
    });

    this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    this.stage.addEventListener('pointermove', (event) => this.onPointerMove(event));
    this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event));
    this.stage.addEventListener('pointercancel', () => this.endDrag());
    this.stage.addEventListener('dragstart', (event) => event.preventDefault()); // no ghost images
    this.stage.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });

    // Theme editor: selecting a deal block shows it.
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.cards.indexOf(event.target.closest('[data-deal]'));
      if (index >= 0) this.go(index, { announce: false });
    });

    this.render({ animate: false });
  }

  onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, dragging: false };
  }

  onPointerMove(event) {
    const pointer = this.pointer;
    if (!pointer || event.pointerId !== pointer.id) return;
    const dx = event.clientX - pointer.x;
    const dy = event.clientY - pointer.y;

    if (!pointer.dragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;
      // Capture only once it's clearly a drag, so a plain click still reaches the card's link.
      pointer.dragging = true;
      this.stage.setPointerCapture(pointer.id);
      this.stage.setAttribute('data-dragging', '');
    }
    // Cards follow the pointer, damped so a long drag doesn't fling them off.
    this.stage.style.setProperty('--deal-drag', `${dx * 0.35}px`);
  }

  onPointerUp(event) {
    const pointer = this.pointer;
    if (!pointer || event.pointerId !== pointer.id) return;
    const dx = event.clientX - pointer.x;
    const wasDragging = pointer.dragging;
    this.endDrag();
    if (!wasDragging) return;
    // Swallow the click that follows a mouse drag; touch drags send none, so clear it right after.
    this.swiped = true;
    setTimeout(() => (this.swiped = false), 0);
    if (Math.abs(dx) > SWIPE_DISTANCE) this.go(this.active + (dx < 0 ? 1 : -1));
  }

  endDrag() {
    if (this.pointer?.dragging && this.stage.hasPointerCapture(this.pointer.id)) {
      this.stage.releasePointerCapture(this.pointer.id);
    }
    this.pointer = null;
    this.stage.removeAttribute('data-dragging');
    this.stage.style.removeProperty('--deal-drag');
  }

  /** Sideways trackpad scroll or Shift + wheel: one deal per gesture. */
  onWheel(event) {
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0;
    if (!delta) return; // vertical scrolling keeps scrolling the page
    event.preventDefault();

    const now = performance.now();
    if (now < (this.wheelLockedUntil || 0)) return;
    if (now - (this.lastWheel || 0) > 200) this.wheelTotal = 0;
    this.lastWheel = now;
    this.wheelTotal = (this.wheelTotal || 0) + delta;

    if (Math.abs(this.wheelTotal) >= WHEEL_DISTANCE) {
      this.go(this.active + (this.wheelTotal > 0 ? 1 : -1));
      this.wheelTotal = 0;
      this.wheelLockedUntil = now + WHEEL_COOLDOWN;
    }
  }

  /**
   * Quick vertical rise-in: when the carousel scrolls into view the visible cards (left, center, right)
   * rise and fade in a beat apart. Skipped for reduced motion and in the theme editor.
   */
  riseOnView() {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.Shopify && window.Shopify.designMode) return;

    this.setAttribute('data-rise', 'pending');
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        const shown = this.cards.filter((card) => !String(card.dataset.pos).startsWith('hidden'));
        shown.sort((a, b) => Number(a.dataset.pos) - Number(b.dataset.pos));
        shown.forEach((card, i) => card.style.setProperty('--rise-i', i));
        this.setAttribute('data-rise', 'play');
        setTimeout(() => {
          this.removeAttribute('data-rise');
          this.cards.forEach((card) => card.style.removeProperty('--rise-i'));
        }, 900);
      },
      { threshold: 0.25 }
    );
    io.observe(this);
  }

  go(index, { announce = true } = {}) {
    const count = this.cards.length;
    const next = ((index % count) + count) % count;
    if (next === this.active) return;
    this.active = next;
    this.render({ animate: true });
    if (announce) this.announce();
  }

  /** Offset of a card from the selected one around the ring, e.g. -1, 0, 1, 2. */
  offsetOf(index) {
    const count = this.cards.length;
    let offset = (((index - this.active) % count) + count) % count;
    if (offset > count / 2) offset -= count;
    return offset;
  }

  render({ animate }) {
    this.cards.forEach((card, index) => {
      const offset = this.offsetOf(index);
      const previous = card.dataset.pos;
      let pos = Math.abs(offset) <= 1 ? String(offset) : offset < 0 ? 'hidden-left' : 'hidden-right';

      // A card leaving the ring fades out on the side it exits from…
      if (animate && pos.startsWith('hidden') && sideOf(previous) !== 0) {
        pos = sideOf(previous) < 0 ? 'hidden-left' : 'hidden-right';
      }
      // …and a card coming back jumps to the far side unseen, then slides into place.
      if (animate && previous && sideOf(previous) * sideOf(pos) < 0) {
        card.setAttribute('data-instant', '');
        card.dataset.pos = sideOf(pos) < 0 ? 'hidden-left' : 'hidden-right';
        void card.offsetWidth;
        card.removeAttribute('data-instant');
      }
      if (!animate) card.setAttribute('data-instant', '');
      card.dataset.pos = pos;
      if (!animate) {
        void card.offsetWidth; // apply the position with transitions off, then turn them back on
        card.removeAttribute('data-instant');
      }

      // Only the selected card is in the tab order / reading order.
      const selected = offset === 0;
      card.toggleAttribute('aria-hidden', !selected);
      card.querySelectorAll('a, button').forEach((el) => {
        if (selected) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', '-1');
      });
    });
  }

  announce() {
    if (!this.status || !this.dataset.statusTemplate) return;
    this.status.textContent = this.dataset.statusTemplate
      .replace('[current]', this.active + 1)
      .replace('[total]', this.cards.length);
  }
}

/** -1 for left positions, 1 for right, 0 for center. */
function sideOf(pos) {
  if (pos === '-1' || pos === 'hidden-left') return -1;
  if (pos === '1' || pos === 'hidden-right') return 1;
  return 0;
}

customElements.define('deal-carousel', DealCarousel);
